import { memo, useContext } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { EventCategory, HitType, useFlagship } from "@flagship.io/react-sdk";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ProductTopBar from "app/pages/Listing/Product/ProductTopBar/ProductTopBar";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";
import ProductCardPricing from "app/pages/.shared/ProductCard/ProductCardPricing";
import MilesEarnedContainer from "app/pages/.shared/MilesEarned/MilesEarnedContainer";
import ProductBadge from "app/features/product/components/ProductBadge/ProductBadge";
import { PrePackageCard, useProductLink } from "app/features/prePackage";
import AppGlobalsContext from "app/AppGlobalsContext";
import {
	BRANDS,
	FLAGSHIP_EVENT_KPIS,
	OFFER_CONTRACT_TYPES,
	PRODUCT_EXPERIENCES_WITH_HOTEL,
	PRODUCT_TAG_CODES,
	TOUR_PRODUCT_TYPES,
} from "app/constants";
import { shouldShowFavoriteBadge } from "app/utils/utils";
import {
	generateDestinationTitle,
	generateHotelName,
	getIsDubaiProduct,
	getUnbeatableOffer,
} from "app/utils/productUtils";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { mapFicheProduitStoreToTag } from "app/utils/analytics-store-mapper";

const ProductCard = ({
	isMobile,
	position,
	product = {},
	travelByTrainAvailable,
	displayCardSliderOnMob = true,
	activeProductsFilters = false,
	variant,
	padding,
	gap,
	showCta = true,
	photoSizes,
}) => {
	const { sendHits } = useFlagship();
	const { brand } = useContext(AppGlobalsContext);
	const { pathname } = useLocation();
	const { track } = useAnalytics();
	const shop = useSelector(state => state.shop);

	const openInNewTab = !isMobile;
	const { to, target, rel } = useProductLink({
		productUri: product.uri,
		openOnNewTab: openInNewTab,
	});

	const badgesToDisplay = product?.badges?.filter(
		badge => badge.code !== PRODUCT_TAG_CODES.COUP_DE_COEUR
	);
	const showEmiratesPassBadge = brand === BRANDS.EK && getIsDubaiProduct(product?.geography);
	const isTourProduct = TOUR_PRODUCT_TYPES.includes(product?.productExperience);
	const unbeatableOfferTag = getUnbeatableOffer(product);
	const isFavorite = shouldShowFavoriteBadge(product);

	const allTags = [
		...(isTourProduct ? ["TOUR_PRODUCT"] : []),
		...(unbeatableOfferTag ? [unbeatableOfferTag] : []),
		...(isFavorite ? [PRODUCT_TAG_CODES.COUP_DE_COEUR] : []),
	];

	const handleProductClick = () => {
		sendHits({
			type: HitType.EVENT,
			category: EventCategory.ACTION_TRACKING,
			action: FLAGSHIP_EVENT_KPIS.CLICK_ON_PP_PRODUCT_CARD,
			label: pathname,
		});

		track("view_offer", {
			position: position + 1,
			tags: allTags,
			product: mapFicheProduitStoreToTag(product),
			startingPrice: product?.fromPriceType?.value ?? 0,
			filters: activeProductsFilters,
		});
	};

	const hasTripAdvisorReviews = product.tripadvisor?.reviewsCount > 0;
	const subtitle = generateHotelName(product.name, product.category);
	const visiblePhotos = displayCardSliderOnMob ? product?.photos : product?.photos?.slice(0, 1);
	const photos = visiblePhotos?.map(photo => ({
		url: photo.url,
		thumbnail: photo.thumbnail,
	}));
	const hasTags = badgesToDisplay?.length > 0 || showEmiratesPassBadge;

	return (
		<PrePackageCard
			data-testid="product"
			to={to}
			target={target}
			rel={rel}
			onClick={handleProductClick}
			title={generateDestinationTitle(shop, product)}
			subtitle={subtitle}
			photos={photos}
			photosLabel={product.name}
			photoSizes={photoSizes}
			truncateSubtitle={hasTripAdvisorReviews}
			badges={
				<ProductTopBar
					isFavorite={isFavorite}
					isTour={isTourProduct}
					{...(unbeatableOfferTag && { unbeatableOfferTag })}
				/>
			}
			rating={
				<ProductRatingContainer
					tripAdvisor={product.tripadvisor || {}}
					holidayCheck={{ ...(product.holidaycheck || {}), size: "small" }}
					isCompact={isMobile}
					hideModal
				/>
			}
			price={
				<ProductCardPricing
					travelByTrainAvailable={travelByTrainAvailable}
					isRental={product?.contractType === OFFER_CONTRACT_TYPES.RENTAL}
					includeOfferWithFlight={product?.includeOfferWithFlight}
					duration={product?.fromPriceType?.numberOfNights}
					publicPrice={product?.fromPriceType?.publicPrice}
					price={product?.fromPriceType?.price}
					hour={product?.expiration?.hour}
					day={product?.expiration?.day}
					type={product?.fromPriceType?.type}
					value={product?.fromPriceType?.value}
					isWithHotel={PRODUCT_EXPERIENCES_WITH_HOTEL.includes(
						product?.productExperience
					)}
				/>
			}
			miles={<MilesEarnedContainer price={product?.fromPriceType?.value} />}
			tags={
				hasTags && (
					<>
						{showEmiratesPassBadge && (
							<ProductBadge
								label={<FormattedMessage id="product.badge.emirates.pass" />}
							/>
						)}
						{badgesToDisplay?.map((badge, index) => (
							<ProductBadge key={index} label={badge?.label} />
						))}
					</>
				)
			}
			ctaLabel={showCta ? <FormattedMessage id="sdp.product.cta.label" /> : undefined}
			compactCta={isMobile}
			variant={variant}
			padding={padding}
			gap={gap}
		/>
	);
};

ProductCard.propTypes = {
	isMobile: PropTypes.bool,
	position: PropTypes.number,
	product: PropTypes.object,
	travelByTrainAvailable: PropTypes.bool,
	displayCardSliderOnMob: PropTypes.bool,
	activeProductsFilters: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
	variant: PropTypes.oneOf(["flat", "elevated", "ghost"]),
	padding: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
	gap: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
	showCta: PropTypes.bool,
	photoSizes: PropTypes.string,
};

export default memo(ProductCard);
